/**
 * Public agent signup form — /diventa-agente
 * Registrazione pubblica di nuovi agenti. Dopo l'invio, l'utente riceve email
 * di conferma e resta in stato pending_approval finché l'admin non lo attiva.
 */
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { CheckCircle2, Loader2, User, MapPin, Briefcase, ShieldCheck, ArrowRight, Sparkles, Wallet } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
  firstName: '', lastName: '', email: '', phone: '', dateOfBirth: '',
  codiceFiscale: '', partitaIVA: '',
  address: '', city: '', province: '', zipcode: '',
  iban: '',
  enasarcoIscritto: false, enasarcoNumero: '',
  experience: '', coverageArea: '', message: '',
  termsAccepted: false, privacyAccepted: false, marketingConsent: false,
};

const AgentSignup = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.termsAccepted || !form.privacyAccepted) {
      setError('Devi accettare Termini e Privacy per procedere.');
      return;
    }
    if (form.enasarcoIscritto && !form.enasarcoNumero.trim()) {
      setError('Inserisci il numero di iscrizione ENASARCO.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/public/agents/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Errore invio candidatura');
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Errore imprevisto');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
        <Header />
        <Helmet><title>Candidatura ricevuta | IlTuoAmaro</title></Helmet>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mb-6 shadow-lg">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Candidatura inviata!</h1>
          <p className="mt-3 text-gray-600 text-lg">
            Grazie {form.firstName}. Ti abbiamo inviato un&apos;email di conferma a <b>{form.email}</b>.
          </p>
          <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 border border-purple-100 text-left">
            <h3 className="font-semibold text-purple-900 mb-3">Cosa succede ora?</h3>
            <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
              <li>Il nostro team valuterà il tuo profilo entro <b>2 giorni lavorativi</b>.</li>
              <li>Una volta approvato, riceverai un&apos;email con le credenziali di accesso al portale agente.</li>
              <li>Da subito potrai gestire clienti, creare preventivi e guadagnare provvigioni.</li>
            </ol>
          </div>
          <Link to="/" className="inline-block mt-8 text-purple-700 hover:underline">← Torna alla home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <Header />
      <Helmet>
        <title>Diventa agente IlTuoAmaro | Guadagna con la private label</title>
        <meta name="description" content="Registrati come agente IlTuoAmaro: gestisci i tuoi clienti, crea preventivi e guadagna provvigioni ricorrenti sui liquori artigianali private label." />
      </Helmet>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-fuchsia-600 to-purple-800" />
        <div className="relative max-w-5xl mx-auto px-4 py-16 sm:py-20 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold mb-4">
            <Sparkles className="w-3 h-3" /> Recruiting agenti aperto
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Diventa agente <br /><span className="bg-gradient-to-r from-yellow-300 to-amber-200 bg-clip-text text-transparent">IlTuoAmaro</span>
          </h1>
          <p className="mt-4 text-lg text-white/90 max-w-2xl">
            Entra nella nostra rete commerciale: liquori artigianali private label per HoReCa, wedding planner, aziende del made in Italy.
            <b> Provvigioni ricorrenti</b>, portale dedicato, materiale marketing incluso.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            {[
              { icon: Wallet, title: 'Fino al 15%', desc: 'di provvigione su ogni ordine' },
              { icon: Briefcase, title: 'Portale dedicato', desc: 'preventivi, ordini, provvigioni in tempo reale' },
              { icon: ShieldCheck, title: 'Zero costi iniziali', desc: 'nessuna quota, nessun vincolo di esclusiva' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
                <Icon className="w-5 h-5 text-yellow-300 mb-2" />
                <div className="font-semibold">{title}</div>
                <div className="text-xs text-white/80 mt-0.5">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 -mt-8 pb-16 relative z-10">
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="agent-signup-form">
          <div className="p-6 sm:p-8 border-b border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900">Registra la tua candidatura</h2>
            <p className="text-sm text-gray-600 mt-1">Compila il form: l&apos;approvazione avviene entro 2 giorni lavorativi.</p>
          </div>

          {/* Sezione 1: Dati personali */}
          <div className="p-6 sm:p-8 space-y-4 border-b border-gray-100">
            <div className="flex items-center gap-2 text-purple-700 font-semibold">
              <User className="w-4 h-4" /> Dati personali
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input required label="Nome *" value={form.firstName} onChange={(v) => set('firstName', v)} testid="signup-firstName" />
              <Input required label="Cognome *" value={form.lastName} onChange={(v) => set('lastName', v)} testid="signup-lastName" />
              <Input required type="email" label="Email *" value={form.email} onChange={(v) => set('email', v)} testid="signup-email" />
              <Input required label="Telefono *" value={form.phone} onChange={(v) => set('phone', v)} testid="signup-phone" placeholder="+39 333 1234567" />
              <Input type="date" label="Data di nascita" value={form.dateOfBirth} onChange={(v) => set('dateOfBirth', v)} testid="signup-dob" />
            </div>
          </div>

          {/* Sezione 2: Dati fiscali */}
          <div className="p-6 sm:p-8 space-y-4 border-b border-gray-100 bg-purple-50/30">
            <div className="flex items-center gap-2 text-purple-700 font-semibold">
              <ShieldCheck className="w-4 h-4" /> Dati fiscali
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input required label="Codice fiscale *" value={form.codiceFiscale} onChange={(v) => set('codiceFiscale', v.toUpperCase())} testid="signup-cf" placeholder="RSSMRA80A01H501U" />
              <Input label="Partita IVA (opzionale)" value={form.partitaIVA} onChange={(v) => set('partitaIVA', v)} testid="signup-piva" placeholder="IT01234567890" />
            </div>
          </div>

          {/* Sezione 3: Residenza */}
          <div className="p-6 sm:p-8 space-y-4 border-b border-gray-100">
            <div className="flex items-center gap-2 text-purple-700 font-semibold">
              <MapPin className="w-4 h-4" /> Residenza
            </div>
            <Input required label="Indirizzo *" value={form.address} onChange={(v) => set('address', v)} testid="signup-address" placeholder="Via Roma 15" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input required label="Città *" value={form.city} onChange={(v) => set('city', v)} testid="signup-city" />
              <Input required label="Provincia *" value={form.province} onChange={(v) => set('province', v.toUpperCase().slice(0, 2))} testid="signup-province" placeholder="MI" maxLength={2} />
              <Input required label="CAP *" value={form.zipcode} onChange={(v) => set('zipcode', v)} testid="signup-zip" maxLength={5} />
            </div>
          </div>

          {/* Sezione 4: Provvigioni + ENASARCO */}
          <div className="p-6 sm:p-8 space-y-4 border-b border-gray-100 bg-purple-50/30">
            <div className="flex items-center gap-2 text-purple-700 font-semibold">
              <Wallet className="w-4 h-4" /> Pagamento provvigioni & ENASARCO
            </div>
            <Input label="IBAN (dove ricevere le provvigioni)" value={form.iban} onChange={(v) => set('iban', v.toUpperCase().replace(/\s/g, ''))} testid="signup-iban" placeholder="IT60X0542811101000000123456" />
            <p className="text-xs text-gray-500">Facoltativo in questa fase, potrai aggiungerlo/modificarlo dal portale dopo l&apos;approvazione.</p>

            <div className="pt-2 border-t border-purple-100">
              <div className="text-xs font-semibold text-gray-700 uppercase mb-2">Iscrizione ENASARCO</div>
              <div className="flex gap-4">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="enasarco"
                    checked={form.enasarcoIscritto === true}
                    onChange={() => set('enasarcoIscritto', true)}
                    data-testid="signup-enasarco-yes"
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500" />
                  Sì, sono iscritto
                </label>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="enasarco"
                    checked={form.enasarcoIscritto === false}
                    onChange={() => { set('enasarcoIscritto', false); set('enasarcoNumero', ''); }}
                    data-testid="signup-enasarco-no"
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500" />
                  No
                </label>
              </div>
              {form.enasarcoIscritto && (
                <div className="mt-3">
                  <Input
                    required
                    label="Numero iscrizione ENASARCO *"
                    value={form.enasarcoNumero}
                    onChange={(v) => set('enasarcoNumero', v)}
                    testid="signup-enasarco-numero"
                    placeholder="Es. 1234567/A"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Sezione 5: Esperienza */}
          <div className="p-6 sm:p-8 space-y-4 border-b border-gray-100">
            <div className="flex items-center gap-2 text-purple-700 font-semibold">
              <Briefcase className="w-4 h-4" /> Esperienza e area operativa
            </div>
            <Input label="Zona operativa" value={form.coverageArea} onChange={(v) => set('coverageArea', v)} testid="signup-area" placeholder="Es. Lombardia, Milano provincia" />
            <Textarea label="Esperienza commerciale (opzionale)" value={form.experience} onChange={(v) => set('experience', v)} testid="signup-experience" rows={3}
              placeholder="Es. 5 anni HoReCa, 20 clienti attivi tra ristoranti e wedding planner, referenze disponibili…" />
            <Textarea label="Messaggio per l'admin (opzionale)" value={form.message} onChange={(v) => set('message', v)} testid="signup-message" rows={2}
              placeholder="Cosa ti spinge a diventare agente IlTuoAmaro?" />
          </div>

          {/* Consensi */}
          <div className="p-6 sm:p-8 space-y-3">
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" required checked={form.termsAccepted} onChange={(e) => set('termsAccepted', e.target.checked)}
                data-testid="signup-terms" className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
              <span>Accetto i <Link to="/termini" className="text-purple-700 hover:underline">Termini di servizio</Link> e il contratto agente *</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" required checked={form.privacyAccepted} onChange={(e) => set('privacyAccepted', e.target.checked)}
                data-testid="signup-privacy" className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
              <span>Ho letto e accetto la <Link to="/privacy" className="text-purple-700 hover:underline">Privacy Policy</Link> (GDPR) *</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.marketingConsent} onChange={(e) => set('marketingConsent', e.target.checked)}
                data-testid="signup-marketing" className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
              <span>Voglio ricevere aggiornamenti su nuovi prodotti e materiali di vendita</span>
            </label>
          </div>

          {error && (
            <div className="mx-6 sm:mx-8 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm" data-testid="signup-error">{error}</div>
          )}

          <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-600">
              Compilando questo form dichiari che i dati forniti sono veritieri. La candidatura è soggetta ad approvazione.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="btn-signup-submit"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl disabled:opacity-60 whitespace-nowrap"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {submitting ? 'Invio in corso…' : 'Invia candidatura'}
            </button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

const Input = ({ label, value, onChange, type = 'text', required, testid, placeholder, maxLength }) => (
  <div>
    <label className="text-xs font-semibold text-gray-700 uppercase">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      maxLength={maxLength}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
    />
  </div>
);

const Textarea = ({ label, value, onChange, rows = 3, testid, placeholder }) => (
  <div>
    <label className="text-xs font-semibold text-gray-700 uppercase">{label}</label>
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y"
    />
  </div>
);

export default AgentSignup;
